// Patient Management System Page (1st Page)

import { useState, useEffect, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import "./PatientForm.css";
import SearchToggle from "./SearchToggle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import BasicDatePicker from "./Date-picker";
import {
  calculateAgeInMonths,
  caluclateAgeinDays,
  caluclateGestationalAgeInDays,
  getDateAndYear,
} from "../helper/getAge";

import {
  antenatalCheckUpsvalues,
  antenatalIllnessOptionValues,
  breastFeedingValues,
  criedAtBirthValues,
  gendersValue,
  hospitalizationValues,
  immunizationValue,
  injectionTetanusToxoidValues,
  ironValues,
  maternalEducationValues,
  modeOfDeliveryvalues,
  mothersNutritionValues,
  neonatalProblemsOptionValues,
  placeOfDeliveryValues,
  relationValues,
  resuscitationValues,
  yesNoOptions,
} from "./selectOptionValues";
import { toast } from "react-toastify";
import SendInvitation from "./SendInvitation";
import NumberInput from "./ui/NumberInput";
import Loader from "./Loader";
import { useNavigate } from "react-router-dom";

const PatientForm = ({ pediatricianId }) => {
  const PATIENTFORM = {
    CAREGIVER: 0,
    PATIENT: 1,
    MATERNAL: 2,
    ANTENATAL: 3,
    BIRTH: 4,
    NEONATAL: 5,
    FAMILY: 6,
    FEEDING: 7,
    PRESENT: 8,
  };
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const CAREGIVERSTATE = {
    caregiverFirstName: "",
    caregiverLastName: "",
    relationship: "",
    phoneNumber: "",
    email: "",
    address: "",
  };

  const PATIENTSTATE = {
    patientFirstName: "",
    patientMiddleName: "",
    patientLastName: "",
    dateOfBirth: null,
    gender: "",
  };

  const MATERNALSTATE = {
    maternalAge: "",
    education: "",
    numberOfChildren: "",
  };

  const ANTENATALSTATE = {
    mothersNutrition: "",
    antenatalCheckUps: "",
    iron: "",
    injectionTetanusToxoid: "",
    antenatalIllness: [],
    edd: null,
  };

  const BIRTHSTATE = {
    placeOfDelivery: "",
    modeOfDelivery: "",
    criedAtBirth: "",
    resuscitation: [],
    congenitalAnomaly: "",
    birthTrauma: "",
    others: "",
    birthWeight: "",
    birthLength: "",
    birthHeadCircumference: "",
    gestationalAge: "",
  };

  const NEONATALSTATE = {
    neonatalProblems: [],
    hospitalization: "",
  };

  const PRESENTSTATE = {
    presentNutrition: "",
    immunization: "",
    chronicIllness: "",
    growthFaltering: "",
    delayedDevelopmentMotor: "",
    delayedDevelopmentCognitive: "",
    delayedDevelopmentLanguage: "",
    atypicalBehaviour: "",
    convulsions: "",
    dysmorphism: "",
    anySystemicIllness: "",
    currentAge: "",
    currentWeight: "",
    currentLength: "",
    currentHeadCircumference: "",
    presentOthers: "",
    currentAgeInDays: "",
    gestationalAgeInMonths: "",
    gestationalAgeInDays: "",
  };

  const TOGGLESTATE = {
    isCongenitalAnomaly: false,
    chronicIllness: false,
    growthFaltering: false,
    delayedDevelopmentMotor: false,
    delayedDevelopmentCognitiveSocial: false,
    delayedDevelopmentLanguage: false,
    atypicalBehaviour: false,
    dysmorphism: false,
    anySystemicIllness: false,
    anyHistoryofIllness: false,
    disabilityInTheFamily: false,
    anyBehaviouralProblem: false,
    birthTrauma: false,
  };

  const FAMILYSTATE = {
    anyHistoryofIllness: "",
    disabilityInTheFamily: "",
    anyBehaviouralProblem: "",
  };

  const FEEDINGSTATE = {
    breastFeeding: "",
    solidFood: "",
    noOfFeeds: "",
  };

  const [step, setStep] = useState(PATIENTFORM.CAREGIVER);
  const [cargiver, setCaregiver] = useState(CAREGIVERSTATE);
  const [patient, setPatient] = useState(PATIENTSTATE);
  const [maternal, setMaternal] = useState(MATERNALSTATE);
  const [antenatal, setAntenatal] = useState(ANTENATALSTATE);
  const [birth, setBirth] = useState(BIRTHSTATE);
  const [neontal, setNeonatal] = useState(NEONATALSTATE);
  const [toggle, setToggle] = useState(TOGGLESTATE);
  const [present, setPresent] = useState(PRESENTSTATE);
  const [family, setFamily] = useState(FAMILYSTATE);
  const [feeding, setFeeding] = useState(FEEDINGSTATE);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("s2stoken");
  const navigate = useNavigate()

  useEffect(() => {
    const gestationalWeeks = birth.gestationalAge;
    const actualAgeInDays = caluclateAgeinDays(patient.dateOfBirth);
    const adjustedAgeDays = actualAgeInDays - (40 - gestationalWeeks) * 7;
    if (!antenatal?.edd && present.currentAge <= 24) {
      console.log("use effect called");
      if (gestationalWeeks === "") {
        setPresent((prev) => ({
          ...prev,
          gestationalAgeInMonths: "",
          gestationalAgeInDays: "",
        }));
      } else if (gestationalWeeks < 37) {
        const gestMonthsDiff = Math.floor((40 - gestationalWeeks) / 4);
        const gestMonths =
          calculateAgeInMonths(patient.dateOfBirth) - gestMonthsDiff;
        setPresent((prev) => ({
          ...prev,
          gestationalAgeInMonths: gestMonths,
          gestationalAgeInDays: adjustedAgeDays,
        }));
      } else {
        setPresent((prev) => ({
          ...prev,
          gestationalAgeInMonths: present.currentAge,
          gestationalAgeInDays: actualAgeInDays,
        }));
      }
    }
  }, [birth.gestationalAge]);

  // Handle date change for Date Picker
  const handleDateChange = (date) => {
    setPatient((prevData) => ({
      ...prevData,
      dateOfBirth: date,
    }));
    const actualAge = caluclateAgeinDays(date);
    const actualAgeInMonths = calculateAgeInMonths(date);
    const calculateAgeInDays = caluclateGestationalAgeInDays(
      date,
      antenatal?.edd
    );
    const gestationalAgeInDays =
      getDateAndYear(date) === getDateAndYear(antenatal?.edd)
        ? actualAge
        : calculateAgeInDays !== 0
        ? actualAge - calculateAgeInDays
        : 0;

    if (antenatal.edd) {
      console.log("edd called");
      const eddAgeIndays = caluclateAgeinDays(antenatal.edd);
      const diffWeeks = Math.round((eddAgeIndays - actualAge) / 7);
      const gestWeeks = 40 + diffWeeks;
      setBirth((prev) => ({
        ...prev,
        gestationalAge: gestWeeks,
      }));
      if (actualAgeInMonths <= 24) {
        if (gestWeeks < 37) {
          const gestMonthsDiff = Math.floor((40 - gestWeeks) / 4);
          const gestMonths = actualAgeInMonths - gestMonthsDiff;
          setPresent((prev) => ({
            ...prev,
            gestationalAgeInMonths: gestMonths,
            gestationalAgeInDays: gestationalAgeInDays,
          }));
        } else {
          setPresent((prev) => ({
            ...prev,
            gestationalAgeInMonths: actualAgeInMonths,
            gestationalAgeInDays: actualAge,
          }));
        }
      } else {
        setPresent((prev) => ({
          ...prev,
          gestationalAgeInMonths: "",
          gestationalAgeInDays: "",
        }));
      }
    }
    if (!antenatal?.edd && birth?.gestationalAge) {
      console.log(" gestational called");
      const gestMonthDiff = Math.floor((40 - birth?.gestationalAge) / 4);
      const adjustedAgeMonths = actualAgeInMonths - gestMonthDiff;
      const adjustedAgeDays = actualAge - (40 - birth?.gestationalAge) * 7;
      if (actualAgeInMonths <= 24) {
        if (birth.gestationalAge < 37) {
          console.log(
            "calculated adjusted months new fomula",
            adjustedAgeMonths
          );
          setPresent((prev) => ({
            ...prev,
            gestationalAgeInMonths: adjustedAgeMonths,
            gestationalAgeInDays: adjustedAgeDays,
          }));
        } else {
          setPresent((prev) => ({
            ...prev,
            gestationalAgeInMonths: actualAgeInMonths,
            gestationalAgeInDays: actualAge,
          }));
        }
      } else {
        setPresent((prev) => ({
          ...prev,
          gestationalAgeInMonths: "",
          gestationalAgeInDays: "",
        }));
      }
    }
    if (actualAgeInMonths === 0) {
      return setPresent((prevDt) => ({
        ...prevDt,
        currentAgeInDays: actualAge,
        currentAge: actualAgeInMonths,
      }));
    }
    setPresent((prevDt) => ({
      ...prevDt,
      currentAge: actualAgeInMonths,
    }));
  };

  const handleEddDateChange = (date) => {
    const actualAge = caluclateAgeinDays(patient.dateOfBirth);
    const actualAgeInMonths = calculateAgeInMonths(patient.dateOfBirth);

    const calculateAgeInDays = caluclateGestationalAgeInDays(
      patient.dateOfBirth,
      date
    );

    const gestationalAgeInDays =
      getDateAndYear(date) === getDateAndYear(patient?.dateOfBirth)
        ? actualAge
        : calculateAgeInDays !== 0
        ? actualAge - calculateAgeInDays
        : 0;
    if (date) {
      const eddAgeIndays = caluclateAgeinDays(date);
      console.log("edd age in days", eddAgeIndays);
      console.log("dob age in days", actualAge);
      const diffWeeks = eddAgeIndays
        ? Math.round((eddAgeIndays - actualAge) / 7)
        : "";
      const gestWeeks = 40 + diffWeeks;
      console.log("diffweeks", diffWeeks);
      console.log("gestweeks", gestWeeks);
      setBirth((prev) => ({
        ...prev,
        gestationalAge: gestWeeks,
      }));
      if (present.currentAge <= 24) {
        if (gestWeeks < 37) {
          const gestMonthsDiff = Math.floor((40 - gestWeeks) / 4);
          const gestMonths = actualAgeInMonths - gestMonthsDiff;
          setPresent((prev) => ({
            ...prev,
            gestationalAgeInMonths: gestMonths,
            gestationalAgeInDays: gestationalAgeInDays,
          }));
        } else {
          setPresent((prev) => ({
            ...prev,
            gestationalAgeInMonths: present.currentAge,
            gestationalAgeInDays: actualAge,
          }));
        }
      } else {
        setPresent((prev) => ({
          ...prev,
          gestationalAgeInMonths: "",
          gestationalAgeInDays: "",
        }));
      }
    } else {
      setBirth((prev) => ({
        ...prev,
        gestationalAge: "",
      }));
      setPresent((prev) => ({
        ...prev,
        gestationalAgeInMonths: "",
        gestationalAgeInDays: "",
      }));
    }

    setAntenatal((prevData) => ({
      ...prevData,
      edd: date,
    }));
  };
  // console.log(present);
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zAZ0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\+?[0-9]{9,15}$/;
    return phoneRegex.test(phone);
  };

  const handleCaregiverChange = (e) => {
    const { name, value } = e.target;
    if (name === "phoneNumber" && isNaN(value)) {
      return toast.warn("Phone number must be a positive integer");
    }
    if (name === "email") {
      setCaregiver({ ...cargiver, [name]: value.toLowerCase().trim() });
    } else if (name === "address") {
      setCaregiver({ ...cargiver, [name]: value });
    } else {
      setCaregiver({ ...cargiver, [name]: value.trim() });
    }
  };

  const handlePatientChange = (e) => {
    if (
      e.target.name === "patientFirstName" ||
      e.target.name === "patientLastName" ||
      e.target.name === "patientMiddleName"
    ) {
      setPatient({ ...patient, [e.target.name]: e.target.value.toLowerCase() });
    } else {
      setPatient({ ...patient, [e.target.name]: e.target.value.trim() });
    }
  };

  const handleMaternalChange = (e) => {
    if (e.target.name === "maternalAge" && isNaN(e.target.value)) {
      return toast.warn(`Mother age must be a positive integer`);
    }
    if (e.target.name === "numberOfChildren" && isNaN(e.target.value)) {
      return toast.warn(`Number of children must be a positive integer`);
    }
    setMaternal({ ...maternal, [e.target.name]: e.target.value });
  };

  const handleAntenatalChange = (e) => {
    setAntenatal({ ...antenatal, [e.target.name]: e.target.value.trim() });
  };

  const handleBirthChange = (e) => {
    if (e.target.name === "birthWeight" && isNaN(e.target.value)) {
      return toast.warn(`Birth weight must be a positive integer`);
    }
    if (e.target.name === "birthLength" && isNaN(e.target.value)) {
      return toast.warn(`Birth length must be a positive integer`);
    }
    if (e.target.name === "birthHeadCircumference" && isNaN(e.target.value)) {
      return toast.warn(`Birth head circumference must be a positive integer`);
    }
    if (e.target.name === "gestationalAge" && isNaN(e.target.value)) {
      return toast.warn(`Gestational age must be a positive integer`);
    }
    if (e.target.name === "others") {
      return setBirth({ ...birth, [e.target.name]: e.target.value });
    }

    setBirth({ ...birth, [e.target.name]: e.target.value.trim() });
  };
  console.log("actual age months", present.currentAge);
  console.log("adjusted age months", present.gestationalAgeInMonths);
  console.log("actual age daya", present.currentAgeInDays);
  console.log("adjusted age days", present.gestationalAgeInDays);

  const handleNeonatalChange = (e) => {
    setNeonatal({ ...neontal, [e.target.name]: e.target.value.trim() });
  };

  const handleFeedingChange = (e) => {
    if (e.target.name === "noOfFeeds" && isNaN(e.target.value)) {
      return toast.warn(`Number of feeds must be a positive integer`);
    }
    setFeeding({ ...feeding, [e.target.name]: e.target.value.trim() });
  };

  const handlePresentChange = (e) => {
    if (e.target.name === "currentWeight" && isNaN(e.target.value)) {
      return toast.warn(`Current Weight must be a positive integer`);
    }
    if (e.target.name === "currentLength" && isNaN(e.target.value)) {
      return toast.warn(`Current length must be a positive integer`);
    }
    if (e.target.name === "currentHeadCircumference" && isNaN(e.target.value)) {
      return toast.warn(
        `Current head circumference must be a positive integer`
      );
    }
    if (
      e.target.name === "presentOthers" ||
      e.target.name === "atypicalBehaviour"
    ) {
      setPresent({ ...present, [e.target.name]: e.target.value });
    } else {
      setPresent({ ...present, [e.target.name]: e.target.value.trim() });
    }
  };

  const handleToggleChange = (e, field) => {
    const { value } = e.target;
    setToggle((prevToggle) => ({
      ...prevToggle,
      [field]: value === "yes",
    }));
    if (value !== "yes") {
      handlePresentChange(e);
    }
  };
  const incrementStep = () => {
    if (step === PATIENTFORM.CAREGIVER && !validateCaregiverFields()) {
      toast.warn("Please fill all required fields.");
      return;
    }
    if (
      step === PATIENTFORM.CAREGIVER &&
      cargiver?.email &&
      !validateEmail(cargiver.email)
    ) {
      toast.warn("Please enter a valid email address");
      return;
    }
    if (
      step === PATIENTFORM.CAREGIVER &&
      !validatePhoneNumber(cargiver.phoneNumber)
    ) {
      toast.warn("Please enter a valid phone number");
      return;
    }
    if (step === PATIENTFORM.PATIENT && !validatePatientFields()) {
      toast.warn("Please fill all required fields.");
      return;
    }
    if (
      step === PATIENTFORM.BIRTH &&
      toggle.isCongenitalAnomaly &&
      birth.congenitalAnomaly.trim() === ""
    ) {
      return toast.warn("Please specify congenital anomaly");
    }
    if (
      step === PATIENTFORM.BIRTH &&
      toggle.birthTrauma &&
      birth.birthTrauma.trim() === ""
    ) {
      return toast.warn("Please specify birth trauma");
    }
    if (
      step === PATIENTFORM.FAMILY &&
      toggle.anyHistoryofIllness &&
      family.anyHistoryofIllness.trim() === ""
    ) {
      return toast.warn("Please specify any history of illness or death");
    }
    if (
      step === PATIENTFORM.FAMILY &&
      toggle.anyBehaviouralProblem &&
      family.anyBehaviouralProblem.trim() === ""
    ) {
      return toast.warn("Please specify  any behavioural problem in family");
    }
    if (
      step === PATIENTFORM.FAMILY &&
      toggle.disabilityInTheFamily &&
      family.disabilityInTheFamily.trim() === ""
    ) {
      return toast.warn("Please specify disability in family");
    }

    if (step === PATIENTFORM.ANTENATAL) {
      console.table(antenatal);
    }

    if (step === PATIENTFORM.PRESENT) {
      if (toggle.chronicIllness && present.chronicIllness.trim() === "") {
        return toast.warn("Please specify chronic illness");
      }
      if (toggle.growthFaltering && present.growthFaltering.trim() === "") {
        return toast.warn("Please specify growth faltering");
      }
      if (
        toggle.delayedDevelopmentCognitiveSocial &&
        present.delayedDevelopmentCognitive.trim() === ""
      ) {
        return toast.warn("Please specify delayed development cognitive");
      }
      if (
        toggle.delayedDevelopmentMotor &&
        present.delayedDevelopmentMotor.trim() === ""
      ) {
        return toast.warn("Please specify delayed development motor");
      }
      if (
        toggle.delayedDevelopmentLanguage &&
        present.delayedDevelopmentLanguage.trim() === ""
      ) {
        return toast.warn("Please specify delayed development language");
      }
      if (toggle.atypicalBehaviour && present.atypicalBehaviour.trim() === "") {
        return toast.warn("Please specify atypical behaviour");
      }
      if (toggle.dysmorphism && present.dysmorphism.trim() === "") {
        return toast.warn("Please specify dysmorphism");
      }
      if (
        toggle.anySystemicIllness &&
        present.anySystemicIllness.trim() === ""
      ) {
        return toast.warn("Please specify systemic illness");
      }
      handleSubmit();
    }
    setStep((prevStep) => {
      const nextStep = Math.min(PATIENTFORM.PRESENT, prevStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return nextStep;
    });
  };

  const decrementStep = () => {
    setStep((prevStep) => {
      const prev = Math.max(PATIENTFORM.CAREGIVER, prevStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return prev;
    });
  };

  const actionLabel = useMemo(() => {
    if (step === PATIENTFORM.PRESENT) {
      return "Submit";
    }
    return "Next";
  }, [PATIENTFORM.PRESENT, step]);

  const secondaryActionLabel = useMemo(() => {
    if (step === PATIENTFORM.CAREGIVER) {
      return undefined;
    }
    return "Back";
  }, [PATIENTFORM.CAREGIVER, step]);

  const validateCaregiverFields = () => {
    return (
      cargiver.caregiverFirstName &&
      cargiver.caregiverLastName &&
      cargiver.relationship &&
      cargiver.phoneNumber &&
      cargiver.address
    );
  };

  const validatePatientFields = () => {
    return (
      patient.patientFirstName &&
      patient.patientLastName &&
      patient.gender &&
      patient.dateOfBirth
    );
  };

  let bodyContent = (
    <div className={"cargiverFormCard"}>
      <h2 className={"cardHeader"}>Caregiver / Parent Information</h2>
      <div className={"caregiverDiveder"}>
        <TextField
          className="small"
          name="caregiverFirstName"
          label="Caregiver First Name"
          type="text"
          size="small"
          value={cargiver.caregiverFirstName}
          onChange={handleCaregiverChange}
          required
        />

        <TextField
          name="caregiverLastName"
          label="Caregiver Last Name"
          type="text"
          size="small"
          value={cargiver.caregiverLastName}
          onChange={handleCaregiverChange}
          required
        />
        <TextField
          select
          name={"relationship"}
          label="Relationship"
          type={"text"}
          size="small"
          value={cargiver.relationship}
          onChange={handleCaregiverChange}
          required
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {relationValues.map((value) => (
            <MenuItem value={value}>{value}</MenuItem>
          ))}
        </TextField>
        <TextField
          name={"email"}
          label="Email"
          type={"text"}
          size="small"
          value={cargiver.email}
          onChange={handleCaregiverChange}
        />
        <NumberInput
          name={"phoneNumber"}
          label="Phone Number"
          value={cargiver.phoneNumber}
          onChange={handleCaregiverChange}
          slotProps={{
            input: {
              inputMode: "numeric",
              pattern: "[0-9]*",
            },
          }}
          required
        />
        <TextField
          name={"address"}
          type="text"
          size="small"
          value={cargiver.address}
          label={"Address"}
          onChange={handleCaregiverChange}
          required
        />
      </div>
    </div>
  );

  let patientForm = (
    <div className={"cargiverFormCard"}>
      <h2 className={"cardHeader"}>Patient Information</h2>
      <div className={"caregiverDiveder"}>
        <TextField
          name={"patientFirstName"}
          label="Patient First Name"
          value={patient.patientFirstName}
          type={"text"}
          size="small"
          onChange={handlePatientChange}
          required
        />
        <TextField
          name={"patientMiddleName"}
          label="Patient Middle Name"
          value={patient.patientMiddleName}
          type={"text"}
          size="small"
          // placeholder={"Patient Middle Name"}
          className={"requiredLabel"}
          onChange={handlePatientChange}
        />
        <TextField
          name={"patientLastName"}
          label="Patient Last Name"
          type={"text"}
          size="small"
          value={patient.patientLastName}
          // placeholder={"Patient Last Name"}
          className={"requiredLabel"}
          onChange={handlePatientChange}
          required
        />
        {/* <div className="calender"> */}
        <BasicDatePicker
          value={patient.dateOfBirth}
          label={"Date of Birth"}
          onChange={handleDateChange}
          required
          maxDate
        />
        {/* {patient.dateOfBirth && <span
            className="clear-calender"
            onClick={() =>
              setPatient((prev) => ({ ...prev, dateOfBirth: null }))
            }
          >
            X
          </span>}
        </div> */}
        <TextField
          type={"text"}
          size="small"
          select
          name={"gender"}
          label="Gender"
          onChange={handlePatientChange}
          value={patient.gender}
          required
          sx={{
            fontSize: "14px",
            height: "40px",
            backgroundColor: "#3071FF21",
          }}
        >
          {gendersValue.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </div>
    </div>
  );

  let maternalForm = (
    <div className={"cargiverFormCard"}>
      <h2 className={"cardHeader"}>Maternal Information</h2>
      <div className={"caregiverDiveder"}>
        <NumberInput
          name={"maternalAge"}
          label="Mother Age"
          value={maternal.maternalAge}
          onChange={handleMaternalChange}
        />
        <TextField
          type={"text"}
          select
          size="small"
          name={"education"}
          label={"Mother Education"}
          onChange={handleMaternalChange}
          value={maternal.education}
          sx={{ backgroundColor: "#3071FF21" }}
        >
          {maternalEducationValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <NumberInput
          name={"numberOfChildren"}
          label="Number Of Children"
          value={maternal.numberOfChildren}
          onChange={handleMaternalChange}
        />
      </div>
    </div>
  );

  const handleBirthTrauamaChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        birthTrauma: true,
      }));
      setBirth((prevBirth) => ({
        ...prevBirth,
        birthTrauma: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        birthTrauma: false,
      }));
      setBirth((prevBirth) => ({
        ...prevBirth,
        birthTrauma: "no",
      }));
    }
  };

  const handleCongenitalAnomalyChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        isCongenitalAnomaly: true,
      }));
      setBirth((prevBirth) => ({
        ...prevBirth,
        congenitalAnomaly: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        isCongenitalAnomaly: false,
      }));
      setBirth((prevBirth) => ({
        ...prevBirth,
        congenitalAnomaly: "no",
      }));
    }
  };

  const handleCongenitalAnomalyInputChange = (e) => {
    const value = e.target.value;
    setBirth((prevBirth) => ({
      ...prevBirth,
      congenitalAnomaly: value,
    }));
  };

  const handleBirthTraumaInputChange = (e) => {
    const value = e.target.value;
    setBirth((prevBirth) => ({
      ...prevBirth,
      birthTrauma: value,
    }));
  };

  let antenatalForm = (
    <div className={"cargiverFormCard"}>
      <h2 className={"cardHeader"}>Antenatal Information</h2>
      <div className={"caregiverDiveder"}>
        <TextField
          type={"text"}
          select
          size="small"
          label={"Mother Nutrition "}
          name={"mothersNutrition"}
          onChange={handleAntenatalChange}
          value={antenatal.mothersNutrition}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {mothersNutritionValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          type={"text"}
          name={"antenatalCheckUps"}
          size="small"
          select
          label={"Antenatal CheckUps"}
          onChange={handleAntenatalChange}
          value={antenatal.antenatalCheckUps}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {antenatalCheckUpsvalues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          type={"text"}
          name={"iron"}
          select
          size="small"
          label={"Iron or Folic Acid "}
          onChange={handleAntenatalChange}
          value={antenatal.iron}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {ironValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type={"text"}
          select
          size="small"
          name={"injectionTetanusToxoid"}
          onChange={handleAntenatalChange}
          value={antenatal.injectionTetanusToxoid}
          label={"Injection Tetanus Toxoid"}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {injectionTetanusToxoidValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <FormControl
          size="small"
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          <InputLabel id="tone">Antenatal Illness</InputLabel>
          <Select
            labelId="responsive-care-giving-label"
            id="responsive-car-giving"
            multiple
            value={antenatal.antenatalIllness}
            onChange={(e) => {
              setAntenatal((prev) => ({
                ...prev,
                antenatalIllness: e.target.value,
              }));
            }}
            input={<OutlinedInput label="Antenatal Illness" />}
            renderValue={(selected) => selected.join(", ")}
            MenuProps={MenuProps}
          >
            {antenatalIllnessOptionValues.map((name) => (
              <MenuItem key={name} value={name} sx={{ width: 400 }}>
                <Checkbox checked={antenatal.antenatalIllness.includes(name)} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className="calender">
          <BasicDatePicker
            value={antenatal.edd}
            label={"Expected Date of Delivery"}
            onChange={handleEddDateChange}
          />
          {antenatal?.edd && (
            <span
              className="clear-calender"
              onClick={() => setAntenatal((prev) => ({ ...prev, edd: null }))}
            >
              X
            </span>
          )}
        </div>
      </div>
    </div>
  );

  let birthForm = (
    <div className={"cargiverFormCard"}>
      <h2 className={"cardHeader"}>Birth Information</h2>
      <div className={"caregiverDiveder"}>
        <TextField
          type={"text"}
          select
          size="small"
          name={"placeOfDelivery"}
          onChange={handleBirthChange}
          label={"Place Of Delivery"}
          value={birth.placeOfDelivery}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {placeOfDeliveryValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={"Mode Of Delivery"}
          size="small"
          select
          type={"text"}
          name={"modeOfDelivery"}
          onChange={handleBirthChange}
          value={birth.modeOfDelivery}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {modeOfDeliveryvalues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label={"Cried At Birth"}
          type={"text"}
          select
          size="small"
          name={"criedAtBirth"}
          onChange={handleBirthChange}
          value={birth.criedAtBirth}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {criedAtBirthValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControl
          size="small"
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          <InputLabel id="tone">Resuscitation</InputLabel>
          <Select
            labelId="Resuscitation"
            id="Resuscitation"
            multiple
            value={birth.resuscitation}
            onChange={(e) => {
              setBirth((prev) => ({
                ...prev,
                resuscitation: e.target.value,
              }));
            }}
            input={<OutlinedInput label="Resuscitation" />}
            renderValue={(selected) => selected.join(", ")}
            MenuProps={MenuProps}
          >
            {resuscitationValues.map((name) => (
              <MenuItem key={name} value={name} sx={{ width: 400 }}>
                <Checkbox checked={birth.resuscitation.includes(name)} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={"Congenital Anomaly"}
          type={"text"}
          select
          size="small"
          name={"congenitalAnomaly"}
          onChange={handleCongenitalAnomalyChange}
          value={toggle.isCongenitalAnomaly ? "yes" : birth.congenitalAnomaly}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {toggle.isCongenitalAnomaly && (
          <TextField
            name={"congenitalAnomaly"}
            label="Specify Congenital Anomaly"
            type={"text"}
            size="small"
            // placeholder={"Specify Congenital Anomaly"}
            className={""}
            onChange={handleCongenitalAnomalyInputChange}
            value={birth.congenitalAnomaly}
          />
        )}
        <NumberInput
          name="birthWeight"
          label="Birth Weight in kg (e.g.,3.50)"
          value={birth.birthWeight}
          onChange={handleBirthChange}
        />
        <NumberInput
          name="birthLength"
          label="Birth Length in cm (e.g., 50.00)"
          value={birth.birthLength}
          onChange={handleBirthChange}
        />
        <NumberInput
          name="birthHeadCircumference"
          label="Birth Head Circumference in cm"
          value={birth.birthHeadCircumference}
          onChange={handleBirthChange}
        />
        <NumberInput
          name="gestationalAge"
          label="Gestational Age in weeks"
          value={birth.gestationalAge}
          size="small"
          onChange={handleBirthChange}
          slotProps={{
            input: {
              readOnly: antenatal?.edd,
            },
          }}
        />
        <TextField
          name={"birthTrauma"}
          type={"text"}
          select
          size="small"
          label={"Birth Trauma"}
          value={toggle.birthTrauma ? "yes" : birth.birthTrauma}
          onChange={handleBirthTrauamaChange}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {toggle.birthTrauma && (
          <TextField
            name={"birthTrauma"}
            type={"text"}
            size="small"
            label={"Specify Birth Trauma"}
            className={""}
            onChange={handleBirthTraumaInputChange}
            value={birth.birthTrauma}
          />
        )}

        <TextField
          name={"others"}
          type={"text"}
          size="small"
          label={"Other birth comments if any"}
          className={""}
          value={birth.others}
          onChange={handleBirthChange}
        />
      </div>
    </div>
  );

  let neonatalForm = (
    <div className={"cargiverFormCard"}>
      <h2 className="cardHeader">Neonatal Period</h2>
      <div className={"caregiverDiveder two-items"}>
        <FormControl
          size="small"
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          <InputLabel id="tone">Neonatal Problems</InputLabel>
          <Select
            labelId="Neonatal Problems"
            id="Neonatal Problems"
            multiple
            value={neontal.neonatalProblems}
            onChange={(e) => {
              setNeonatal((prev) => ({
                ...prev,
                neonatalProblems: e.target.value,
              }));
            }}
            input={<OutlinedInput label="Neonatal Problems" />}
            renderValue={(selected) => selected.join(", ")}
            MenuProps={MenuProps}
          >
            {neonatalProblemsOptionValues.map((name) => (
              <MenuItem key={name} value={name} sx={{ width: 400 }}>
                <Checkbox checked={neontal.neonatalProblems.includes(name)} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={"Hospitalization"}
          type={"text"}
          select
          size="small"
          name={"hospitalization"}
          onChange={handleNeonatalChange}
          value={neontal.hospitalization}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {hospitalizationValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </div>
    </div>
  );

  const handleAnyHistoryOfIillnessChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        anyHistoryofIllness: true,
      }));
      setFamily((prevBirth) => ({
        ...prevBirth,
        anyHistoryofIllness: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        anyHistoryofIllness: false,
      }));
      setFamily((prevBirth) => ({
        ...prevBirth,
        anyHistoryofIllness: "no",
      }));
    }
  };

  const handleAnyBehavProblemChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        anyBehaviouralProblem: true,
      }));
      setFamily((prevBirth) => ({
        ...prevBirth,
        anyBehaviouralProblem: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        anyBehaviouralProblem: false,
      }));
      setFamily((prevBirth) => ({
        ...prevBirth,
        anyBehaviouralProblem: "no",
      }));
    }
  };

  const handleAnyDevelopmentChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        disabilityInTheFamily: true,
      }));
      setFamily((prevBirth) => ({
        ...prevBirth,
        disabilityInTheFamily: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        disabilityInTheFamily: false,
      }));
      setFamily((prevBirth) => ({
        ...prevBirth,
        disabilityInTheFamily: "no",
      }));
    }
  };

  const handleAnyHistoryOfIillnessInputChange = (e) => {
    const value = e.target.value;
    setFamily((prevBirth) => ({
      ...prevBirth,
      anyHistoryofIllness: value,
    }));
  };

  const handleAnyBehavProblemInputChange = (e) => {
    const value = e.target.value;
    setFamily((prevBirth) => ({
      ...prevBirth,
      anyBehaviouralProblem: value,
    }));
  };

  const handleDisbailityInFamilyInputChange = (e) => {
    const value = e.target.value;
    setFamily((prevBirth) => ({
      ...prevBirth,
      disabilityInTheFamily: value,
    }));
  };

  let familyForm = (
    <div className={"cargiverFormCard"}>
      <h2 className={"cardHeader"}>Family History</h2>
      <div className={"familyDiveder"}>
        <TextField
          type={"text"}
          select
          size="small"
          label={"Any history of illness or death in Sibling ?"}
          name={"anyHistoryofIllness"}
          onChange={handleAnyHistoryOfIillnessChange}
          value={
            toggle.anyHistoryofIllness ? "yes" : family.anyHistoryofIllness
          }
          options={yesNoOptions}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {toggle.anyHistoryofIllness && (
          <TextField
            name={"anyHistoryofIllness"}
            label="Specify history of illness or death"
            size="small"
            type={"text"}
            className={""}
            value={family.anyHistoryofIllness}
            onChange={handleAnyHistoryOfIillnessInputChange}
          />
        )}
        <TextField
          type={"text"}
          name={"anyBehaviouralProblem"}
          select
          size="small"
          label={"Any behavioural problem in the family?"}
          onChange={handleAnyBehavProblemChange}
          value={
            toggle.anyBehaviouralProblem ? "yes" : family.anyBehaviouralProblem
          }
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {toggle.anyBehaviouralProblem && (
          <TextField
            name={"anyBehaviouralProblem"}
            label="Specify any behavioural problem in the family"
            type={"text"}
            size="small"
            value={family.anyBehaviouralProblem}
            className={""}
            onChange={handleAnyBehavProblemInputChange}
          />
        )}
        <TextField
          type={"text"}
          name={"disabilityInTheFamily"}
          size="small"
          select
          label={"Any developmental challenge/disability in the family ?"}
          onChange={handleAnyDevelopmentChange}
          value={
            toggle.disabilityInTheFamily ? "yes" : family.disabilityInTheFamily
          }
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {toggle.disabilityInTheFamily && (
          <TextField
            name={"disabilityInTheFamily"}
            label="Specify any developmental challenge in the family"
            type={"text"}
            size="small"
            // placeholder={
            //   "Specify Any developmental challenge / disability in the family "
            // }
            value={family.disabilityInTheFamily}
            className={""}
            onChange={handleDisbailityInFamilyInputChange}
          />
        )}
      </div>
    </div>
  );

  let feedingForm = (
    <div className={"cargiverFormCard"}>
      <h2 className={"cardHeader"}>Feeding Information</h2>
      <div className={"caregiverDiveder"}>
        <TextField
          name={"breastFeeding"}
          value={feeding.breastFeeding}
          type={"text"}
          select
          size="small"
          label={"Breast Feeding"}
          onChange={handleFeedingChange}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {breastFeedingValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <NumberInput
          name={"noOfFeeds"}
          label="No. of feeds or meals per day"
          value={feeding.noOfFeeds}
          size="small"
          onChange={handleFeedingChange}
        />
        <TextField
          name={"solidFood"}
          value={feeding.solidFood}
          type={"text"}
          select
          size="small"
          label={"Solid Food or Semi-solids "}
          className={""}
          onChange={handleFeedingChange}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {breastFeedingValues.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </div>
    </div>
  );

  const handleChronicIillnessChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        chronicIllness: true,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        chronicIllness: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        chronicIllness: false,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        chronicIllness: "no",
      }));
    }
  };

  const handleGrowthFlateeringChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        growthFaltering: true,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        growthFaltering: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        growthFaltering: false,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        growthFaltering: "no",
      }));
    }
  };

  const handleDelayedDevCogChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        delayedDevelopmentCognitiveSocial: true,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        delayedDevelopmentCognitive: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        delayedDevelopmentCognitiveSocial: false,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        delayedDevelopmentCognitive: "no",
      }));
    }
  };

  const handleDelayedDevMotorChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        delayedDevelopmentMotor: true,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        delayedDevelopmentMotor: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        delayedDevelopmentMotor: false,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        delayedDevelopmentMotor: "no",
      }));
    }
  };

  const handleDealyedDevLangChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        delayedDevelopmentLanguage: true,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        delayedDevelopmentLanguage: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        delayedDevelopmentLanguage: false,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        delayedDevelopmentLanguage: "no",
      }));
    }
  };

  const handleAtypicalBehavChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        atypicalBehaviour: true,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        atypicalBehaviour: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        atypicalBehaviour: false,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        atypicalBehaviour: "no",
      }));
    }
  };

  const handleDysoChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        dysmorphism: true,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        dysmorphism: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        dysmorphism: false,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        dysmorphism: "no",
      }));
    }
  };

  const handleAnySystemIllnessChange = (e) => {
    const value = e.target.value;
    if (value === "yes") {
      setToggle((prevToggle) => ({
        ...prevToggle,
        anySystemicIllness: true,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        anySystemicIllness: "",
      }));
    } else {
      setToggle((prevToggle) => ({
        ...prevToggle,
        anySystemicIllness: false,
      }));
      setPresent((prevBirth) => ({
        ...prevBirth,
        anySystemicIllness: "no",
      }));
    }
  };

  const handleChronicIilnessInputChange = (e) => {
    const value = e.target.value;
    setPresent((prevBirth) => ({
      ...prevBirth,
      chronicIllness: value,
    }));
  };

  const handleGrowthFlateringInputChange = (e) => {
    const value = e.target.value;
    setPresent((prevBirth) => ({
      ...prevBirth,
      growthFaltering: value,
    }));
  };

  const handleDelayedDevCogInputChange = (e) => {
    const value = e.target.value;
    setPresent((prevBirth) => ({
      ...prevBirth,
      delayedDevelopmentCognitive: value,
    }));
  };

  const handleDelayedDevMotorInputChange = (e) => {
    const value = e.target.value;
    setPresent((prevBirth) => ({
      ...prevBirth,
      delayedDevelopmentMotor: value,
    }));
  };

  const handleDelayedDevLangInputChange = (e) => {
    const value = e.target.value;
    setPresent((prevBirth) => ({
      ...prevBirth,
      delayedDevelopmentLanguage: value,
    }));
  };

  const handleAtypicalBehavInputChange = (e) => {
    const value = e.target.value;
    setPresent((prevBirth) => ({
      ...prevBirth,
      atypicalBehaviour: value,
    }));
  };

  const handleDysoInputChange = (e) => {
    const value = e.target.value;
    setPresent((prevBirth) => ({
      ...prevBirth,
      dysmorphism: value,
    }));
  };

  const handleAnySystemIllnessInputChange = (e) => {
    const value = e.target.value;
    setPresent((prevBirth) => ({
      ...prevBirth,
      anySystemicIllness: value,
    }));
  };

  let presentForm = (
    <div className={"cargiverFormCard"}>
      <h2 className={"cardHeader"}>Present Health Status</h2>
      <div className={"presentDiveder"}>
        <TextField
          className="text"
          type="text"
          size="small"
          label={
            present.currentAge === "" || present.currentAge === 0
              ? "Actual age in days"
              : "Actual age in months"
          }
          name="currentAge"
          value={
            present.currentAge === "" || present.currentAge === 0
              ? present?.currentAgeInDays + " Days"
              : present?.currentAge
          }
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
        {present?.currentAge <= 24 && (
          <TextField
            name="adjustedAge"
            label={
              (present.gestationalAgeInMonths === "" ||
                present.gestationalAgeInMonths === 0) &&
              (present.gestationalAgeInDays !== "" || present.currentAge === 0)
                ? "Adjusted age in days"
                : "Adjusted age in months"
            }
            size="small"
            value={
              present.gestationalAgeInDays !== ""
                ? //  && present.gestationalAgeInDays !== 0
                  present.gestationalAgeInMonths === "" ||
                  present.gestationalAgeInMonths === 0
                  ? `${present.gestationalAgeInDays} Days`
                  : present.gestationalAgeInMonths
                : "N/A"
            }
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
        )}
        <NumberInput
          label="Current weight in kg (e.g. 3.50)"
          name="currentWeight"
          value={present.currentWeight}
          onChange={handlePresentChange}
        />
        <NumberInput
          label="Current length in cm (e.g. 50)"
          name="currentLength"
          value={present.currentLength}
          onChange={handlePresentChange}
        />
        <NumberInput
          label="Current head circumference cm"
          name="currentHeadCircumference"
          value={present.currentHeadCircumference}
          onChange={handlePresentChange}
        />
      </div>
      <div className={"caregiverDiveder"}>
        <TextField
          className="item"
          label={"Immunization"}
          type={"text"}
          select
          size="small"
          name={"immunization"}
          onChange={handlePresentChange}
          value={present.immunization}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {immunizationValue.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          className="item"
          type={"text"}
          select
          size="small"
          label={"Chronic Illness "}
          name={"chronicIllness"}
          onChange={handleChronicIillnessChange}
          value={toggle.chronicIllness ? "yes" : present.chronicIllness}
          options={yesNoOptions}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {toggle.chronicIllness && (
          <TextField
            className="item"
            value={present.chronicIllness}
            name={"chronicIllness"}
            type={"text"}
            size="small"
            label="Specify Chronic Illness"
            //   placeholder={"Specify Chronic Illness"}
            onChange={handleChronicIilnessInputChange}
          />
        )}
      </div>
      <div className={"caregiverDiveder two-items"}>
        <TextField
          type={"text"}
          name={"growthFaltering"}
          select
          size="small"
          label={"Growth Faltering"}
          onChange={handleGrowthFlateeringChange}
          value={toggle.growthFaltering ? "yes" : present.growthFaltering}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {toggle.growthFaltering && (
          <TextField
            name={"growthFaltering"}
            type={"text"}
            size="small"
            label="Specify Growth Faltering"
            //   placeholder={"Specify Growth Faltering"}
            className={""}
            value={present.growthFaltering}
            onChange={handleGrowthFlateringInputChange}
          />
        )}
        <TextField
          type={"text"}
          select
          size="small"
          label={"Delayed Development Cognitive"}
          name={"delayedDevelopmentCognitive"}
          onChange={handleDelayedDevCogChange}
          value={
            toggle.delayedDevelopmentCognitiveSocial
              ? "yes"
              : present.delayedDevelopmentCognitive
          }
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {toggle.delayedDevelopmentCognitiveSocial && (
          <TextField
            name={"delayedDevelopmentCognitive"}
            type={"text"}
            size="small"
            label="Specify Delayed Development Cognitive"
            //   placeholder={"Specify Delayed Development Cognitive"}
            className={""}
            onChange={handleDelayedDevCogInputChange}
            value={present.delayedDevelopmentCognitive}
          />
        )}
        <TextField
          type={"text"}
          select
          size="small"
          label={"Delayed Development Motor"}
          name={"delayedDevelopmentMotor"}
          onChange={handleDelayedDevMotorChange}
          value={
            toggle.delayedDevelopmentMotor
              ? "yes"
              : present.delayedDevelopmentMotor
          }
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {toggle.delayedDevelopmentMotor && (
          <TextField
            name={"delayedDevelopmentMotor"}
            type={"text"}
            size="small"
            label="Specify Delayed Development Motor"
            //   placeholder={"Specify Delayed Development Motor"}
            className={""}
            value={present.delayedDevelopmentMotor}
            onChange={handleDelayedDevMotorInputChange}
          />
        )}
        <TextField
          type={"text"}
          size="small"
          select
          label="Delayed Development Language "
          name={"delayedDevelopmentLanguage"}
          onChange={handleDealyedDevLangChange}
          value={
            toggle.delayedDevelopmentLanguage
              ? "yes"
              : present.delayedDevelopmentLanguage
          }
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {toggle.delayedDevelopmentLanguage && (
          <TextField
            name={"delayedDevelopmentLanguage"}
            type={"text"}
            size="small"
            label="Specify Delayed Development Language"
            //   placeholder={"Specify Delayed Development Language "}
            className={""}
            onChange={handleDelayedDevLangInputChange}
            value={present.delayedDevelopmentLanguage}
          />
        )}
        <TextField
          type={"text"}
          label={"Atypical Behaviour"}
          select
          size="small"
          name={"atypicalBehaviour"}
          onChange={handleAtypicalBehavChange}
          value={toggle.atypicalBehaviour ? "yes" : present.atypicalBehaviour}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {toggle.atypicalBehaviour && (
          <TextField
            name={"atypicalBehaviour"}
            type={"text"}
            size="small"
            label="Specify Atypical Behaviour"
            // placeholder={"Specify Atypical Behaviour"}
            className={""}
            onChange={handlePresentChange}
            value={present.atypicalBehaviour}
          />
        )}
      </div>
      <div className={"caregiverDiveder"}>
        <TextField
          label={"Convulsions"}
          type={"text"}
          select
          size="small"
          name={"convulsions"}
          onChange={handlePresentChange}
          value={present.convulsions}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label={"Dysmorphism"}
          size="small"
          type={"text"}
          name={"dysmorphism"}
          onChange={handleDysoChange}
          value={toggle.dysmorphism ? "yes" : present.dysmorphism}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {toggle.dysmorphism && (
          <TextField
            name={"dysmorphism"}
            type={"text"}
            size="small"
            label="Specify Dysmorphism"
            //   placeholder={"Specify Dysmorphism "}
            className={""}
            onChange={handleDysoInputChange}
            value={present.dysmorphism}
          />
        )}
        <TextField
          type={"text"}
          select
          size="small"
          label={"Any Systemic Illness "}
          name={"anySystemicIllness"}
          onChange={handleAnySystemIllnessChange}
          value={toggle.anySystemicIllness ? "yes" : present.anySystemicIllness}
          sx={{ backgroundColor: "#3071FF21", height: "40px" }}
        >
          {yesNoOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {toggle.anySystemicIllness && (
          <TextField
            name={"anySystemicIllness"}
            type={"text"}
            size="small"
            label="Specify Any Systemic Illness"
            //   placeholder={"Specify Any Systemic Illness"}
            className={""}
            onChange={handleAnySystemIllnessInputChange}
            value={present.anySystemicIllness}
          />
        )}
        <TextField
          type="text"
          size="small"
          label="Any other comments"
          name="presentOthers"
          value={present.presentOthers}
          // placeholder="Others"
          onChange={handlePresentChange}
        />
      </div>
    </div>
  );

  // To check the pediatrician id being passed, we can toast.warn it as below
  useEffect(() => {
    if (pediatricianId) {
      //  toast.warn(`Pediatrician ID: ${pediatricianId}`); // This will now work
    }
  }, [pediatricianId]);

  const [activeTab, setActiveTab] = useState("sendinvitation");
  const [formData, setFormData] = useState({
    caregiverFirstName: "",
    caregiverLastName: "",
    relationship: "",
    phoneNumber: "",
    email: "",
    address: "",
    patientFirstName: "",
    patientMiddleName: "",
    patientLastName: "",
    dateOfBirth: null,
    gender: "",
    birthWeight: "",
    birthLength: "",
    birthHeadCircumference: "",
    gestationalAge: "",
    birthMedicalHistory: "",
    currentAge: "",
    currentWeight: "",
    currentHeight: "",
    currentHeadCircumference: "",
    currentMedicalConditions: "",
  });

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    // Reset the QR code when switching to "Search Patient"
 
  };

  // Function to validate email

  const handleSubmit = async () => {
    console.log("Form submitted with data:", formData);
    // Check if all required fields are filled

    const antenatalIllnessValues = antenatal.antenatalIllness;
    const resuscitationValues = birth.resuscitation;
    const neonatalProblemsValues = neontal.neonatalProblems;

    const payload = {
      pediatricianId,
      ...cargiver,
      relationship: cargiver.relationship,
      ...patient,
      ...maternal,
      ...antenatal,
      ...birth,
      ...present,
      patientFirstName: patient.patientFirstName.trim(),
      patientMiddleName: patient?.patientMiddleName.trim(),
      patientLastName: patient.patientLastName.trim(),
      ...neontal,
      ...family,
      ...feeding,
      neonatalProblems: neonatalProblemsValues,
      antenatalCheckups: antenatal.antenatalCheckUps,
      resuscitation: resuscitationValues,
      antenatalIllness: antenatalIllnessValues,
      nutrition: antenatal.mothersNutrition,
      dateOfBirth: patient.dateOfBirth
        ? new Date(patient.dateOfBirth).toLocaleDateString("en-CA")
        : null,
      edd: antenatal.edd
        ? new Date(antenatal.edd).toLocaleDateString("en-CA")
        : null,
    };

    console.log("Payload:", payload);
    console.log("Payload:", antenatal.edd);
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/add_patient_by_doctor`,
        {
          method: "POST",
          //mode : "cors",
          headers: {
            "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          credentials: "include",
        }
      );

      const result = await response.json();
      console.log(result);
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to add patient");
      }

      if (response.ok) {
        setLoading(false);
        toast.success("Patient added successfully");
        // Reset form data to empty values
        setFormData({
          caregiverFirstName: "",
          caregiverLastName: "",
          relationship: "",
          phoneNumber: "",
          email: "",
          address: "",
          patientFirstName: "",
          patientMiddleName: "",
          patientLastName: "",
          dateOfBirth: "",
          gender: "",
          birthWeight: "",
          birthLength: "",
          birthHeadCircumference: "",
          gestationalAge: "",
          birthMedicalHistory: "",
          currentAge: "",
          currentWeight: "",
          currentHeight: "",
          currentHeadCircumference: "",
          currentMedicalConditions: "",
        });
        setStep(PATIENTFORM.CAREGIVER);
        setCaregiver(CAREGIVERSTATE);
        setPatient(PATIENTSTATE);
        setMaternal(MATERNALSTATE);
        setAntenatal(ANTENATALSTATE);
        setBirth(BIRTHSTATE);
        setNeonatal(NEONATALSTATE);
        setPresent(PRESENTSTATE);
        setFamily(FAMILYSTATE);
        setFeeding(FEEDINGSTATE);
      } else {
        setLoading(false);
        toast.error(
          result?.message || "An error occurred while adding the patient."
        );
      }
    } catch (error) {
       if (error?.message === 'Token has expired') {
          localStorage.removeItem('s2stoken')
          navigate('/')
        }
      setLoading(false);
      console.error("Error adding patient:", error);
      toast.error("Failed to add patient. Please try again later.");
    }
  };


  return (
    <>
      <Loader isLoading={loading} />
      <div className="patient-profile">
        {/* Tab Section */}
        <div className="toggle-tabs">
          {/* <div
            className={`toggle-tab ${
              activeTab === "addPatient" ? "active" : ""
            }`}
            onClick={() => handleTabClick("addPatient")}
          >
            ADD PATIENT
          </div>
          <div
            className={`toggle-tab ${
              activeTab === "searchPatient" ? "active" : ""
            }`}
            onClick={() => handleTabClick("searchPatient")}
          >
            SEARCH PATIENT
          </div> */}
          <div
            className={`toggle-tab ${
              activeTab === "sendinvitation" ? "active" : ""
            }`}
            onClick={() => handleTabClick("sendinvitation")}
          >
            SEND INVITATION
          </div>
        </div>

        {/* Add Patient Section */}
        {activeTab === "addPatient" && (
          <>
            {/* <h2>Patient Management System</h2> */}
            {step === PATIENTFORM.CAREGIVER && bodyContent}
            {step === PATIENTFORM.PATIENT && patientForm}
            {step === PATIENTFORM.MATERNAL && maternalForm}
            {step === PATIENTFORM.ANTENATAL && antenatalForm}
            {step === PATIENTFORM.BIRTH && birthForm}
            {step === PATIENTFORM.NEONATAL && neonatalForm}
            {step === PATIENTFORM.FAMILY && familyForm}
            {step === PATIENTFORM.FEEDING && feedingForm}
            {step === PATIENTFORM.PRESENT && presentForm}
            <div className={"actionButtons"}>
              {secondaryActionLabel && (
                <button
                  type="button"
                  className={"secondaryActionButton"}
                  onClick={decrementStep}
                >
                  {secondaryActionLabel}
                </button>
              )}
              {actionLabel && (
                <button
                  className={"actionButton"}
                  onClick={incrementStep}
                  type="button"
                >
                  {actionLabel}
                </button>
              )}
            </div>
          </>
        )}

        {/* Search Patient Section */}
        {activeTab === "searchPatient" && (
          <SearchToggle pediatricianId={pediatricianId} />
        )}
        {activeTab === "sendinvitation" && (
          <SendInvitation pediatricianId={pediatricianId} />
        )}  
      </div>
    </>
  );
};

export default PatientForm;
